/**
 * Fibo: cSquare's nautilus-shell trail guide mascot.
 *
 * Voice convention (project memory): in-product copy uses a "Fibo says:"
 * labeled callout via <FiboSays>. Brand/about copy uses they/them, never "he".
 *
 * Poses are illustrated assets in src/assets/fibo/. The .png files are the
 * 1024px masters (the downloadable logo kit ships those); the .webp files
 * are the 512px web copies imported here. Add new poses there and
 * extend the FiboPose union below; keep the same line weight + palette so the
 * character reads as one mascot across surfaces.
 */
import { cn } from "@/lib/utils";
import wave from "@/assets/fibo/fibo-wave.webp";
import point from "@/assets/fibo/fibo-point.webp";
import celebrate from "@/assets/fibo/fibo-celebrate.webp";
import think from "@/assets/fibo/fibo-think.webp";
import reassure from "@/assets/fibo/fibo-reassure.webp";
import map from "@/assets/fibo/fibo-map.webp";
import clipboard from "@/assets/fibo/fibo-clipboard.webp";
import telescope from "@/assets/fibo/fibo-telescope.webp";
import read from "@/assets/fibo/fibo-read.webp";
import summit from "@/assets/fibo/fibo-summit.webp";
import sleep from "@/assets/fibo/fibo-sleep.webp";
import shield from "@/assets/fibo/fibo-shield.webp";

export type FiboPose =
  | "wave"
  | "point"
  | "celebrate"
  | "think"
  | "reassure"
  | "map"
  | "clipboard"
  | "telescope"
  | "read"
  | "summit"
  | "sleep"
  | "shield";

const POSES: Record<FiboPose, string> = {
  wave,
  point,
  celebrate,
  think,
  reassure,
  map,
  clipboard,
  telescope,
  read,
  summit,
  sleep,
  shield,
};

const ALT: Record<FiboPose, string> = {
  wave: "Fibo, the cSquare trail guide, waving hello",
  point: "Fibo pointing the way forward",
  celebrate: "Fibo celebrating a milestone",
  think: "Fibo thinking it through",
  reassure: "Fibo offering a reassuring gesture",
  map: "Fibo reading a trail map at a crossing",
  clipboard: "Fibo handing over a checklist clipboard",
  telescope: "Fibo scanning the horizon with a telescope",
  read: "Fibo studying an open book",
  summit: "Fibo planting a flag at the summit",
  sleep: "Fibo resting curled up in their shell",
  shield: "Fibo holding a protective shield",
};

interface FiboProps {
  pose?: FiboPose;
  size?: number;
  className?: string;
  /** Set true when Fibo is purely decorative next to a labeled "Fibo says:" callout. */
  decorative?: boolean;
  /** Horizontally mirror the rendered pose. Useful when Fibo sits to the right of
   *  referenced content and the "point" pose would otherwise point away from it. */
  flip?: boolean;
}

export function Fibo({ pose = "wave", size = 96, className, decorative = false, flip = false }: FiboProps) {
  return (
    <span
      className={cn("inline-block shrink-0 select-none", className)}
      style={{ width: size, height: size }}
      aria-hidden={decorative || undefined}
    >
      <img
        src={POSES[pose]}
        alt={decorative ? "" : ALT[pose]}
        loading="lazy"
        decoding="async"
        className="block w-full h-full object-contain"
        style={flip ? { transform: "scaleX(-1)" } : undefined}
      />
    </span>
  );
}

interface FiboSaysProps {
  children: React.ReactNode;
  pose?: FiboPose;
  className?: string;
}

/**
 * Labeled coaching callout: the canonical way Fibo "speaks" in-product.
 * Renders Fibo on the left and a "Fibo says:" prefixed message on the right.
 */
export function FiboSays({ children, pose = "point", className }: FiboSaysProps) {
  return (
    <aside
      className={cn(
        "flex items-start gap-3 border border-primary/20 bg-primary/5 p-4",
        className,
      )}
      role="note"
    >
      <Fibo pose={pose} size={56} decorative className="shrink-0" />
      <div className="text-sm text-foreground leading-relaxed">
        <span className="font-semibold text-primary mr-1">Fibo says:</span>
        <span>{children}</span>
      </div>
    </aside>
  );
}

export default Fibo;
